import { useState } from 'react'
import { AreaTexto, Campo, Entrada } from '@/components/ui/Campo'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { ModalMarco, mensajeDeError } from '@/features/admin/ModalMarco'
import {
  LAMINAS,
  TOPE_COPY,
  enlaceDrive,
  leerArchivosPegados,
} from '@/lib/schemas/publicaciones'
import type { PanelPublicacion } from '@/lib/schemas/revision'
import type { ArchivoDeDrive } from '@/lib/drive/useElegirDeDrive'
import { useCrearVersion } from './apiRevision'
import { BotonDrive, ListaArchivos } from './ArchivosDeDrive'

interface ModalNuevaVersionProps {
  abierto: boolean
  alCerrar: () => void
  publicacion: PanelPublicacion
  /** Carpeta de Drive del cliente: ahí se abre el selector (Fase 6). */
  carpetaDrive?: string | null
}

/**
 * C5 · bloque "Nueva versión" (§8.5). El copy y los archivos llegan
 * precargados de la versión actual. Desde `cambios`, enviarla deja la
 * publicación aprobada (§7.2.1) y el pie lo dice.
 */
export function ModalNuevaVersion(props: ModalNuevaVersionProps) {
  if (!props.abierto) return null
  return <Formulario key={props.publicacion.versionActual} {...props} />
}

function Formulario({
  abierto,
  alCerrar,
  publicacion,
  carpetaDrive = null,
}: ModalNuevaVersionProps) {
  const crear = useCrearVersion(publicacion.id)
  const avisar = useAvisar()
  const laminas = publicacion.archivos.filter((a) => a.rol !== 'portada')
  const portadaActual = publicacion.archivos.find((a) => a.rol === 'portada')

  const [copy, setCopy] = useState(publicacion.copy)
  const [archivosTexto, setArchivosTexto] = useState(
    laminas.map((a) => enlaceDrive(a.driveFileId)).join('\n'),
  )
  const [portadaTexto, setPortadaTexto] = useState(
    portadaActual ? enlaceDrive(portadaActual.driveFileId) : '',
  )
  const [error, setError] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [deDrive, setDeDrive] = useState<Record<string, ArchivoDeDrive>>({})
  const recordar = (lista: ArchivoDeDrive[]) => {
    setDeDrive((a) => ({ ...a, ...Object.fromEntries(lista.map((x) => [x.id, x])) }))
  }
  const comoTexto = (ids: string[]) => ids.map(enlaceDrive).join('\n')

  const archivos = leerArchivosPegados(archivosTexto)
  const portada = leerArchivosPegados(portadaTexto)
  const desdeCambios = publicacion.estado === 'cambios'
  const usados = portada.ids[0] ? [...archivos.ids, portada.ids[0]] : archivos.ids
  const rechazados = usados.filter((id) => deDrive[id]?.validacion.estado === 'rechazo')

  const guardar = (enviar: boolean) => {
    if (archivos.invalidos.length > 0) {
      setError('Pegá enlaces de Drive o sus identificadores.')
      return
    }
    if (archivos.ids.length === 0) {
      setError('Elegí los archivos de la versión nueva.')
      return
    }
    if (
      publicacion.tipo === 'carrusel' &&
      (archivos.ids.length < LAMINAS.min || archivos.ids.length > LAMINAS.max)
    ) {
      setError(
        `Un carrusel lleva de ${String(LAMINAS.min)} a ${String(LAMINAS.max)} láminas.`,
      )
      return
    }
    if (publicacion.tipo === 'video' && !portada.ids[0]) {
      setError('El video necesita una portada.')
      return
    }
    if (rechazados.length > 0) {
      setError('Hay archivos que no se pueden publicar: cambialos o quitalos.')
      return
    }

    setOcupado(true)
    setError(null)
    crear
      .mutateAsync({
        copy,
        archivos: archivos.ids,
        portada: portada.ids[0] ?? '',
        enviar,
        metadatos: Object.fromEntries(
          usados.flatMap((id) => {
            const d = deDrive[id]
            return d ? [[id, d.metadato]] : []
          }),
        ),
      })
      .then((r) => {
        avisar(
          r.aprobacionAutomatica
            ? `Versión ${String(r.numero)} enviada. La publicación quedó aprobada.`
            : `Versión ${String(r.numero)} guardada`,
        )
        alCerrar()
      })
      .catch((e: unknown) => {
        setError(mensajeDeError(e))
      })
      .finally(() => {
        setOcupado(false)
      })
  }

  return (
    <ModalMarco
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={`Nueva versión (v${String(publicacion.versionActual + 1)})`}
      descripcion={
        desdeCambios
          ? 'Al enviar, la publicación queda aprobada.'
          : 'La versión nueva reemplaza a la actual; la anterior se conserva.'
      }
      guardar={desdeCambios ? 'Enviar y aprobar' : 'Guardar versión'}
      ocupado={ocupado}
      error={error}
      alCambiar={() => {
        setError(null)
      }}
      alEnviar={() => {
        guardar(true)
      }}
    >
      <BotonDrive
        carpetaId={carpetaDrive}
        multiple={publicacion.tipo === 'carrusel'}
        espera={publicacion.tipo === 'video' ? 'video' : 'imagen'}
        titulo="Elegí los archivos de la versión nueva"
        alElegir={(lista) => {
          recordar(lista)
          const nuevos = lista.map((x) => x.id)
          // Una versión nueva reemplaza los archivos: se arranca de lo elegido.
          setArchivosTexto(
            comoTexto(publicacion.tipo === 'carrusel' ? nuevos : nuevos.slice(0, 1)),
          )
        }}
      />

      <Campo
        etiqueta="Archivos"
        ayuda="Cada versión apunta a un archivo nuevo en Drive: no reemplaces el de la versión anterior."
        extremo={
          archivos.ids.length > 0
            ? `${String(archivos.ids.length)} ${archivos.ids.length === 1 ? 'archivo' : 'archivos'}`
            : undefined
        }
      >
        {(p) => (
          <AreaTexto
            id={p.id}
            aria-describedby={p.describedBy}
            value={archivosTexto}
            onChange={(e) => {
              setArchivosTexto(e.target.value)
            }}
            rows={publicacion.tipo === 'carrusel' ? 4 : 2}
            // Más alta en escritorio: con dos renglones no se distinguían los
            // enlaces de Drive entre sí (Serena, 22/09). En celular, igual.
            className="sm:min-h-40"
          />
        )}
      </Campo>

      <ListaArchivos
        ids={archivos.ids}
        info={Object.fromEntries(
          archivos.ids.map((id) => {
            const d = deDrive[id]
            return [id, d ? { nombre: d.nombre, validacion: d.validacion } : undefined]
          }),
        )}
        carrusel={publicacion.tipo === 'carrusel'}
        privados={new Set()}
        alReordenar={(ids) => {
          setArchivosTexto(comoTexto(ids))
        }}
        alQuitar={(id) => {
          setArchivosTexto(comoTexto(archivos.ids.filter((x) => x !== id)))
        }}
      />

      {publicacion.tipo === 'video' && (
        <BotonDrive
          carpetaId={carpetaDrive}
          multiple={false}
          espera="imagen"
          titulo="Elegí la portada"
          etiqueta="Elegir portada de Drive"
          alElegir={(lista) => {
            recordar(lista)
            const id = lista[0]?.id
            if (id) setPortadaTexto(enlaceDrive(id))
          }}
        />
      )}

      {publicacion.tipo === 'video' && (
        <Campo etiqueta="Portada">
          {(p) => (
            <Entrada
              id={p.id}
              value={portadaTexto}
              onChange={(e) => {
                setPortadaTexto(e.target.value)
              }}
            />
          )}
        </Campo>
      )}

      <Campo etiqueta="Copy" extremo={`${String(copy.length)} / ${String(TOPE_COPY)}`}>
        {(p) => (
          <AreaTexto
            id={p.id}
            value={copy}
            rows={5}
            maxLength={TOPE_COPY}
            onChange={(e) => {
              setCopy(e.target.value)
            }}
          />
        )}
      </Campo>

      {!desdeCambios && (
        <button
          type="button"
          className="self-start text-sm text-tinta-suave underline underline-offset-4"
          onClick={() => {
            guardar(false)
          }}
        >
          Guardar sin enviar
        </button>
      )}
    </ModalMarco>
  )
}
